import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { adjustDateStringToInput, isValidDateRange } from 'utils/dateUtils';

interface IUseDateRangeProps {
  minDate?: Date;
  maxDate?: Date;
  time?: boolean;
}

export interface IUseInputDateRange {
  start: IUseDateInputProps;
  end: IUseDateInputProps;
  resetDates: () => void;
  isStartAfterOrEqual: boolean;
}

interface IUseDateInputProps {
  value: string;
  min: string;
  max: string;
  onChange: (date: string) => void;
}

export const useDateInputRange = ({
  minDate,
  maxDate,
  time,
}: IUseDateRangeProps): IUseInputDateRange => {
  const [min, setMin] = useState(JSON.stringify(minDate) ?? '');
  const [max, setMax] = useState(JSON.stringify(maxDate) ?? '');

  const [startValue, setStartValue] = useState<string>(min);
  const [startMin, setStartMin] = useState<string>(min);
  const [startMax, setStartMax] = useState<string>(max);

  const [endValue, setEndValue] = useState<string>(max);
  const [endMin, setEndMin] = useState<string>(min);
  const [endMax, setEndMax] = useState<string>(max);

  const changeStart = (date: string) => {
    const isValid = isValidDateRange(date, endValue);

    if (!endValue.length || isValid) {
      setStartValue(date);
      setEndMin(date);
    }
    if (!!endValue.length && !isValid) {
      setStartValue(endValue);
    }
  };

  const changeEnd = (date: string) => {
    const isValid = isValidDateRange(startValue, date);

    if (!startValue.length || isValid) {
      setEndValue(date);
      setStartMax(date);
    }
    if (!!startValue.length && !isValid) {
      setEndValue(startValue);
    }
  };

  const resetDates = () => {
    setStartValue(min);
    setStartMin(min);
    setStartMax(max);

    setEndValue(max);
    setEndMin(min);
    setEndMax(max);
  };

  const isStartAfterOrEqual = !isValidDateRange(startValue, endValue);

  useEffect(() => {
    setStartValue((prev) => adjustDateStringToInput(prev, time));
  }, [startValue]);

  useEffect(() => {
    setStartMin((prev) => adjustDateStringToInput(prev, time));
  }, [startMin]);

  useEffect(() => {
    setStartMax((prev) => adjustDateStringToInput(prev, time));
  }, [startMax]);

  useEffect(() => {
    setEndValue((prev) => adjustDateStringToInput(prev, time));
  }, [endValue]);

  useEffect(() => {
    setEndMin((prev) => adjustDateStringToInput(prev, time));
  }, [endMin]);

  useEffect(() => {
    setEndMax((prev) => adjustDateStringToInput(prev, time));
  }, [endMax]);

  useEffect(() => {
    console.log(minDate);

    const minDateString = JSON.stringify(minDate);

    if (
      (!!minDate && !!min.length && isValidDateRange(minDateString, min)) ||
      (!!minDate && !min.length)
    ) {
      console.log('min changed');
      setMin(minDateString);
      if (isValidDateRange(startValue, minDateString) || !startValue.length) {
        console.log('start changed');
        setStartValue(minDateString);
        setStartMin(minDateString);
      }
    }
  }, [minDate]);

  useEffect(() => {
    if (!!maxDate && isValidDateRange(JSON.stringify(maxDate), max)) {
      setMax(JSON.stringify(maxDate));
    }
  }, [maxDate]);

  const start: IUseDateInputProps = {
    max: startMax,
    min: startMin,
    onChange: changeStart,
    value: startValue,
  };

  const end: IUseDateInputProps = {
    max: endMax,
    min: endMin,
    onChange: changeEnd,
    value: endValue,
  };

  return {
    start,
    end,
    resetDates,
    isStartAfterOrEqual,
  };
};

export function getDateXDaysEarlier(daysNum: number) {
  const date = new Date();
  date.setDate(date.getDate() - daysNum);

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}T00:00:00.000Z`;
}

export function computeFormDisplay(
  Form_base_last: number,
  last_session_date: Date,
  today: Date,
  k: number = 0.5
): number {
  const days_without_training =
    (today.getTime() - last_session_date.getTime()) / (1000 * 60 * 60 * 24);

  let form_display = Form_base_last - k * days_without_training;
  if (form_display < 0) form_display = 0;
  if (form_display > 100) form_display = 100;

  return form_display;
}

function startOfDay(d: Date) {
  const date = new Date(d);
  date.setHours(0, 0, 0, 0);
  return date;
}

function endOfDay(d: Date) {
  const date = new Date(d);
  date.setHours(23, 59, 59, 999);
  return date;
}

function getNearestMonday(d: Date) {
  const day = d.getDay();
  const norm = day === 0 ? 7 : day;
  const monday = new Date(d);
  monday.setDate(d.getDate() - (norm - 1));
  return startOfDay(monday);
}

function addDays(d: Date, days: number) {
  const tmp = new Date(d);
  tmp.setDate(tmp.getDate() + days);
  return tmp;
}

///////////////////

function startOfDayUTC(d: Date) {
  const date = new Date(
    Date.UTC(d.getFullYear(), d.getMonth(), d.getDate(), 0, 1, 0, 0)
  );
  return date;
}

export function endOfDayUTC(d: Date) {
  const date = new Date(
    Date.UTC(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 0, 0)
  );
  return date;
}

// Ostatnie 7 dni + trend

export function getLast7DaysData(today: Date = new Date()) {
  const currentMon = getNearestMonday(today);
  const isMonday = today.getDay() === 1;

  const displayStart = isMonday
    ? startOfDayUTC(today)
    : startOfDayUTC(currentMon);
  const displayEnd = isMonday ? endOfDayUTC(today) : endOfDayUTC(today);

  const trendEnd = addDays(currentMon, -1);
  const trendStart = addDays(trendEnd, -6);

  return {
    display: {
      from: displayStart.toISOString(),
      to: displayEnd.toISOString(),
    },
    trend: {
      from: startOfDayUTC(trendStart).toISOString(),
      to: endOfDayUTC(trendEnd).toISOString(),
    },
  };
}

//  Ostatnie 30 dni

export function getLast30DaysData(today: Date = new Date()) {
  const start = addDays(today, -29);
  const end = today;

  return {
    from: startOfDayUTC(start).toISOString(),
    to: endOfDayUTC(end).toISOString(),
  };
}

//  Aktualny mikrocykl

export function getCurrentMicrocycle(today: Date = new Date()) {
  const currentMon = getNearestMonday(today);
  const isMonday = today.getDay() === 1;

  const displayStart = startOfDayUTC(currentMon);
  const displayEnd = isMonday ? endOfDayUTC(currentMon) : endOfDayUTC(today);

  const trendStart = addDays(currentMon, -7);
  const trendEnd = addDays(today, -7);

  return {
    display: {
      from: displayStart.toISOString(),
      to: displayEnd.toISOString(),
    },
    trend: {
      from: startOfDayUTC(trendStart).toISOString(),
      to: endOfDayUTC(trendEnd).toISOString(),
    },
  };
}

//  Poprzedni mikrocykl

export function getPreviousMicrocycle(today: Date = new Date()) {
  const currentMon = getNearestMonday(today);

  const prevMon = addDays(currentMon, -7);
  const prevSun = addDays(prevMon, 6);

  const trendStart = addDays(prevMon, -7);
  const trendEnd = addDays(trendStart, 6);

  return {
    display: {
      from: startOfDayUTC(prevMon).toISOString(),
      to: endOfDayUTC(prevSun).toISOString(),
    },
    trend: {
      from: startOfDayUTC(trendStart).toISOString(),
      to: endOfDayUTC(trendEnd).toISOString(),
    },
  };
}
